import React, { useState, useEffect, useCallback } from 'react';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import { api } from '../../api/client';

const NAVY = '#0C2D48';
const BORDER = '#e0e0e0';
const BG = '#f8f9fb';
const ACCENT = '#5352ed';
const GREEN = '#27ae60';
const AMBER = '#f59e0b';
const RED = '#E96A6A';
const FONT = "'Raleway', sans-serif";

function fmtDuration(minutes) {
  if (!minutes) return '0m';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m > 0 ? ` ${m}m` : ''}`.trim() : `${m}m`;
}

function isoWeekLabel(dateStr) {
  const d = new Date(dateStr + 'T00:00');
  const day = d.getDay() || 7;
  const mon = new Date(d);
  mon.setDate(d.getDate() - day + 1);
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  const fmt = dt => dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return `${fmt(mon)} – ${fmt(sun)}`;
}

function isoWeekKey(dateStr) {
  const d = new Date(dateStr + 'T00:00');
  const day = d.getDay() || 7;
  const mon = new Date(d);
  mon.setDate(d.getDate() - day + 1);
  return mon.toISOString().slice(0, 10);
}

export default function ProjectTimeTab({ project }) {
  const { currentUser } = useCurrentUser();
  const isPro = ['Pro', 'Team', 'Enterprise'].includes(currentUser?.plan);
  const isTeam = ['Team', 'Enterprise'].includes(currentUser?.plan);

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!project?.id || !isPro) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api(`/api/time-entries?projectId=${project.id}`);
      if (!res.ok) throw new Error('Failed to load time entries');
      const data = await res.json();
      setEntries(data.entries || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [project?.id, isPro]);

  useEffect(() => { load(); }, [load]);

  if (!isPro) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', fontFamily: FONT }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>⏱</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: NAVY, marginBottom: 8 }}>Time tracking is a Pro feature</div>
        <div style={{ fontSize: 13, color: '#888', marginBottom: 20, textAlign: 'center', maxWidth: 300 }}>
          Upgrade to Pro or Team to track time against projects and view detailed reports.
        </div>
        <button
          style={{ padding: '0.6rem 1.5rem', background: NAVY, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: FONT }}
        >
          Upgrade Plan
        </button>
      </div>
    );
  }

  const totalMins = entries.reduce((s, e) => s + (e.duration || 0), 0);
  const budgetMins = (project?.budgetHours || 0) * 60;
  const budgetPct = budgetMins > 0 ? Math.min(100, Math.round((totalMins / budgetMins) * 100)) : null;

  // Group by week
  const byWeek = new Map();
  for (const e of entries) {
    const wk = isoWeekKey(e.startTime.slice(0, 10));
    if (!byWeek.has(wk)) byWeek.set(wk, { label: isoWeekLabel(e.startTime.slice(0, 10)), minutes: 0 });
    byWeek.get(wk).minutes += e.duration || 0;
  }
  const weeks = [...byWeek.entries()].sort((a, b) => b[0].localeCompare(a[0]));

  // Group by userId (Team plan only)
  const byUser = new Map();
  if (isTeam) {
    for (const e of entries) {
      const uid = e.userId;
      if (!byUser.has(uid)) byUser.set(uid, { name: e.userName || uid.slice(0, 8), minutes: 0 });
      byUser.get(uid).minutes += e.duration || 0;
    }
  }
  const members = [...byUser.entries()].sort((a, b) => b[1].minutes - a[1].minutes);

  return (
    <div style={{ padding: '24px', fontFamily: FONT, maxWidth: 820 }}>

      {/* Summary cards */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 28, flexWrap: 'wrap' }}>
        <SummaryCard label="Total logged" value={fmtDuration(totalMins)} color={NAVY} />
        {budgetPct !== null && (
          <SummaryCard
            label={`Budget (${project.budgetHours}h)`}
            value={`${budgetPct}%`}
            color={budgetPct >= 90 ? RED : budgetPct >= 70 ? AMBER : GREEN}
          />
        )}
        <SummaryCard label="Entries" value={entries.length} color={ACCENT} />
      </div>

      {/* Budget bar */}
      {budgetPct !== null && (
        <div style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#888', marginBottom: 6 }}>
            <span>Budget usage</span>
            <span>{fmtDuration(totalMins)} / {project.budgetHours}h</span>
          </div>
          <div style={{ height: 10, borderRadius: 5, background: BORDER, overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 5,
              background: budgetPct >= 90 ? RED : budgetPct >= 70 ? AMBER : GREEN,
              width: `${budgetPct}%`,
              transition: 'width 0.4s',
            }} />
          </div>
          {budgetPct >= 90 && (
            <div style={{ fontSize: 12, color: RED, fontWeight: 600, marginTop: 6 }}>
              ⚠ {budgetPct >= 100 ? 'Budget exceeded' : 'Approaching budget limit'}
            </div>
          )}
        </div>
      )}

      {loading && (
        <div style={{ color: '#aaa', fontSize: 14, padding: '20px 0' }}>Loading…</div>
      )}

      {error && (
        <div style={{ background: 'rgba(220,38,38,0.07)', border: '1px solid rgba(220,38,38,0.25)', borderRadius: 8, padding: '0.6rem 0.85rem', color: RED, fontSize: 13, marginBottom: 20 }}>
          {error}
        </div>
      )}

      {!loading && entries.length === 0 && !error && (
        <div style={{ textAlign: 'center', padding: '48px 20px', color: '#bbb' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>⏱</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#aaa' }}>No time logged yet</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>Start a timer or log time manually on any task in this project.</div>
        </div>
      )}

      {entries.length > 0 && (
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>

          {/* By week */}
          <div style={{ flex: '1 1 280px' }}>
            <SectionHeader>By week</SectionHeader>
            <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 10, overflow: 'hidden' }}>
              {weeks.map(([wk, { label, minutes }]) => (
                <div key={wk} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderBottom: `1px solid ${BORDER}` }}>
                  <span style={{ fontSize: 13, color: NAVY }}>{label}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: NAVY }}>{fmtDuration(minutes)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* By member (Team plan) */}
          {isTeam && members.length > 0 && (
            <div style={{ flex: '1 1 280px' }}>
              <SectionHeader>By team member</SectionHeader>
              <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 10, overflow: 'hidden' }}>
                {members.map(([uid, { name, minutes }]) => {
                  const pct = totalMins > 0 ? Math.round((minutes / totalMins) * 100) : 0;
                  return (
                    <div key={uid} style={{ padding: '10px 16px', borderBottom: `1px solid ${BORDER}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: NAVY }}>{name}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: NAVY }}>{fmtDuration(minutes)}</span>
                      </div>
                      <div style={{ height: 4, borderRadius: 2, background: BORDER, overflow: 'hidden' }}>
                        <div style={{ height: '100%', borderRadius: 2, background: ACCENT, width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, color }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 10, padding: '14px 20px', minWidth: 130 }}>
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 12, color: '#888', marginTop: 3 }}>{label}</div>
    </div>
  );
}

function SectionHeader({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
      {children}
    </div>
  );
}
